import { getTenant, getTenantApiHost } from '@config/tenant'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useBackNav from '@hooks/useBackNav'

/**
 * Some specialized reports (CB Payments dashboards, Exago Advanced
 * Reports) are external/embedded products inside the legacy CB shell.
 * Reimplementing them in React is out of scope and not useful — they
 * already render their own UI. Provide a clear handoff that opens the
 * legacy URL in a new tab. The user is already authenticated (their CB
 * cookie is set via TokenLogin), so the legacy view will load directly.
 */
const LegacyEmbedReport = ({ name, summary, mvcPath, message, openLabel, noTenantMessage }) => {
  useDocumentTitle(name)
  // Back returns to where the user came from, else the report catalog. Bare '/reports' is
  // Dashboards once that beta is on (CC-49980), which is where this arrow used to land.
  const goBack = useBackNav()
  const host = getTenantApiHost()
  const legacyUrl = `https://${host}/web/${mvcPath}`
  /*
   * Spec 214 review: on the shared host the tenant is the site the user picked, and
   * `/login?changesite=1` clears it while the user stays signed in — so the host above
   * becomes ".centerbase.com" and the link leads nowhere. A page that states what to do
   * instead (`noTenantMessage`) draws no link and no URL then. Pages that do not state
   * it render exactly as before.
   */
  const unlinked = !getTenant() && !!noTenantMessage

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={goBack}
          aria-label="Back to reports"
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-display text-gray-900">{name}</h1>
          {summary && <p className="text-sm text-gray-500 mt-0.5">{summary}</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="max-w-md mx-auto">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Opens in legacy view</h3>
          <p className="text-sm text-gray-600 mb-6">
            {message || `${name} is an embedded dashboard that lives in the legacy Centerbase app. Open it in a new tab — you're already signed in.`}
          </p>
          {unlinked ? (
            <p className="text-sm text-gray-600" data-testid="legacy-embed-no-tenant">{noTenantMessage}</p>
          ) : (
            <>
              {/* The primary CTA: white on #1d4ed8, 6.70:1 (src/index.css B1). It was white on
                  the decorative accent #479eff, 2.76:1. The unlayered .btn-cb-primary sets its
                  own display, padding, radius, weight and fill, so no utility sits beside it. */}
              <a
                href={legacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cb-primary"
              >
                {openLabel || 'Open in legacy view'}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
              {/* --color-rc-muted #6b7280 on this white card: 4.83:1 (gray-400 was 2.60:1). */}
              <p className="text-xs text-rc-muted mt-4 break-all">{legacyUrl}</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Convenience factories for the registry
export const cbPaymentsPaymentsReport = (props) => (
  <LegacyEmbedReport {...props}
    name="CB Payments — Payments"
    summary="Centerbase Payments dashboard"
    mvcPath="CBPayments/Payments"
    message="The Payments dashboard is provided by the Centerbase Payments service. Open it in the legacy app to view, refund, or reconcile payments."
  />
)
export const cbPaymentsPayoutsReport = (props) => (
  <LegacyEmbedReport {...props}
    name="CB Payments — Payouts"
    summary="Settlement payouts from Centerbase Payments"
    mvcPath="CBPayments/Payouts"
    message="Payout schedules and history are managed inside the Centerbase Payments service."
  />
)
export const cbPaymentsReconciliationReport = (props) => (
  <LegacyEmbedReport {...props}
    name="CB Payments — Reconciliation"
    summary="Reconcile CB Payments transactions"
    mvcPath="cbPaymentsReconciliation/Home"
    message="Use the legacy reconciliation view to match Centerbase Payments transactions to their bank statements."
  />
)
export const exagoReport = (props) => (
  <LegacyEmbedReport {...props}
    name="Advanced Reports"
    summary="Custom report designer (Exago BI)"
    // Spec 214 §3.6: Core's Advanced Reports page is the hash route `cbExagoReportsHomeView`
    // opens (Enumerators.js), not an MVC action.
    mvcPath="#advanced-reports-home"
    openLabel="Open Exago"
    noTenantMessage="Open Advanced Reports from Centerbase."
    message="Advanced Reports use Exago, an embedded BI engine. Custom report design and execution remain in the legacy Centerbase app for now."
  />
)

export default LegacyEmbedReport
